import { Platform, Text, type StyleProp, type TextStyle } from "react-native";
import { EMBER } from "../../onboarding/theme";
import {
  FACT_SHEET_LINK_TERMS,
  type FactSheetLinkTarget,
  type FactSheetLinkTerm,
} from "./factSheetLinks";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type LinkedTextMatch = {
  start: number;
  end: number;
  text: string;
  target: FactSheetLinkTarget;
};

function isWordChar(value?: string): boolean {
  return Boolean(value && /[A-Za-z0-9]/.test(value));
}

function hasTextBoundary(text: string, start: number, length: number): boolean {
  const before = text[start - 1];
  const after = text[start + length];
  return !isWordChar(before) && !isWordChar(after);
}

function getTextMatches({
  text,
  currentBlockId,
  terms,
}: {
  text: string;
  currentBlockId?: string;
  terms: FactSheetLinkTerm[];
}): LinkedTextMatch[] {
  const lowerText = text.toLowerCase();
  const activeTerms = terms
    .flatMap((entry) =>
      entry.terms.map((term) => ({
        term,
        target: entry.target,
      })),
    )
    .filter(({ term, target }) => target.id !== currentBlockId && term.length)
    .sort((a, b) => b.term.length - a.term.length);

  const matches: LinkedTextMatch[] = [];
  let index = 0;

  while (index < text.length) {
    const match = activeTerms.find(({ term }) => {
      if (!lowerText.startsWith(term.toLowerCase(), index)) return false;
      return hasTextBoundary(text, index, term.length);
    });

    if (!match) {
      index += 1;
      continue;
    }

    matches.push({
      start: index,
      end: index + match.term.length,
      text: text.slice(index, index + match.term.length),
      target: match.target,
    });
    index += match.term.length;
  }

  return matches;
}

export default function LinkedFactSheetText({
  text,
  currentBlockId,
  onOpenLink,
  style,
  terms = FACT_SHEET_LINK_TERMS,
}: {
  text: string;
  currentBlockId?: string;
  onOpenLink?: (target: FactSheetLinkTarget) => void;
  style?: StyleProp<TextStyle>;
  terms?: FactSheetLinkTerm[];
}) {
  const capturePress = usePressCapture("LinkedFactSheetText");
  const matches = onOpenLink
    ? getTextMatches({ text, currentBlockId, terms })
    : [];

  if (!matches.length) {
    return (
      <Text className="font-dm-sans" style={style}>
        {text}
      </Text>
    );
  }

  const parts: {
    key: string;
    text: string;
    target?: FactSheetLinkTarget;
  }[] = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    if (match.start > cursor) {
      parts.push({
        key: `plain-${cursor}-${match.start}`,
        text: text.slice(cursor, match.start),
      });
    }

    parts.push({
      key: `link-${match.start}-${index}`,
      text: match.text,
      target: match.target,
    });
    cursor = match.end;
  });

  if (cursor < text.length) {
    parts.push({
      key: `plain-${cursor}-${text.length}`,
      text: text.slice(cursor),
    });
  }

  return (
    <Text className="font-dm-sans" style={style}>
      {parts.map((part) => {
        if (!part.target) return <Text key={part.key}>{part.text}</Text>;

        return (
          <Text
            key={part.key}
            onPress={() => {
              // The matched term comes from the authored fact-sheet link
              // table, never from patient-entered text.
              capturePress(pressLabel(part.text), "fact_sheet_inline_link");
              onOpenLink?.(part.target!);
            }}
            style={
              {
                color: EMBER.colors.textLight,
                backgroundColor: "rgba(214, 184, 151, 0.24)",
                borderRadius: 5,
                paddingHorizontal: 3,
                paddingVertical: 1,
                overflow: "hidden",
                ...(Platform.OS === "web" && {
                  backgroundImage:
                    "radial-gradient(rgba(123, 98, 77, 0.11) 0.55px, transparent 0.55px)",
                  backgroundSize: "4px 4px",
                }),
              } as Record<string, unknown>
            }
          >
            {part.text}
          </Text>
        );
      })}
    </Text>
  );
}

export type { FactSheetLinkTarget };
